import { useState, useEffect } from 'react';
import { Plus, X, ChevronDown, ChevronUp, Workflow, Zap, Users, AlertTriangle } from 'lucide-react';
import { Button, Card } from '@centraoneplus/ui';
import { ticketTypesService } from '@/services/ticket-types.service';
import type { TicketType } from '@centraoneplus/shared-types';
import type {
  AIResolutionConfig,
  IntentWorkflowMapping,
  AI_INTENTS,
} from '@/types/pipeline';
import { ThemedSelect } from '@components/common/ThemedSelect';

interface ResolutionRoutingSectionProps {
  config: AIResolutionConfig | undefined;
  workflows: { id: string; name: string }[];
  teams: { id: string; name: string }[];
  onChange: (config: AIResolutionConfig) => void;
}

const defaultResolutionConfig: AIResolutionConfig = {
  intentWorkflowMappings: [],
  ticketTypeWorkflowMappings: [],
  availableMcpTools: [],
  confidenceThreshold: 0.6,
  sentimentHandoffThreshold: -0.5,
  vipAlwaysHandoff: false,
  maxResolutionAttempts: 3,
};

// AI intents from the AI Engine
const AI_INTENT_LIST = [
  { id: 'question', label: 'Question', description: 'General questions' },
  { id: 'complaint', label: 'Complaint', description: 'Customer complaints' },
  { id: 'request_support', label: 'Request Support', description: 'Support requests' },
  { id: 'request_feature', label: 'Request Feature', description: 'Feature requests' },
  { id: 'report_bug', label: 'Report Bug', description: 'Bug reports' },
  { id: 'request_refund', label: 'Request Refund', description: 'Refund requests' },
  { id: 'update_account', label: 'Update Account', description: 'Account updates' },
  { id: 'cancel_service', label: 'Cancel Service', description: 'Cancellation requests' },
  { id: 'get_information', label: 'Get Information', description: 'Information requests' },
  { id: 'provide_feedback', label: 'Provide Feedback', description: 'Customer feedback' },
  { id: 'track_order', label: 'Track Order', description: 'Order tracking' },
];

// Available MCP tools (these would come from the MCP server in production)
const AVAILABLE_MCP_TOOLS = [
  { id: 'knowledge_search', label: 'Knowledge Search', description: 'Search knowledge base' },
  { id: 'order_lookup', label: 'Order Lookup', description: 'Look up order details' },
  { id: 'customer_info', label: 'Customer Info', description: 'Get customer information' },
  { id: 'account_update', label: 'Account Update', description: 'Update account details' },
  { id: 'ticket_create', label: 'Ticket Create', description: 'Create new ticket' },
  { id: 'refund_process', label: 'Refund Process', description: 'Process refunds' },
  { id: 'subscription_manage', label: 'Subscription Manage', description: 'Manage subscriptions' },
];

export default function ResolutionRoutingSection({
  config,
  workflows,
  teams,
  onChange,
}: ResolutionRoutingSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeSection, setActiveSection] = useState<'intents' | 'ticketTypes' | 'tools' | 'handoff'>(
    'intents'
  );
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);

  // Fetch active ticket types for the Ticket-Type → workflow editor (non-fatal).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const types = await ticketTypesService.getTicketTypes({ active: true });
        if (!cancelled) setTicketTypes(types);
      } catch (err) {
        console.error('Error fetching ticket types for resolution routing:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const resolutionConfig = config ?? defaultResolutionConfig;
  const ticketTypeMappings = resolutionConfig.ticketTypeWorkflowMappings ?? [];

  const handleIntentWorkflowChange = (intent: string, workflowId: string | undefined) => {
    const existingMappings = resolutionConfig.intentWorkflowMappings.filter(
      (m) => m.intent !== intent
    );

    if (workflowId) {
      const workflow = workflows.find((w) => w.id === workflowId);
      onChange({
        ...resolutionConfig,
        intentWorkflowMappings: [
          ...existingMappings,
          { intent, workflowId, workflowName: workflow?.name, priority: 10 },
        ],
      });
    } else {
      onChange({
        ...resolutionConfig,
        intentWorkflowMappings: existingMappings,
      });
    }
  };

  const handleToolToggle = (toolId: string) => {
    const currentTools = resolutionConfig.availableMcpTools || [];
    if (currentTools.includes(toolId)) {
      onChange({
        ...resolutionConfig,
        availableMcpTools: currentTools.filter((t) => t !== toolId),
      });
    } else {
      onChange({
        ...resolutionConfig,
        availableMcpTools: [...currentTools, toolId],
      });
    }
  };

  const getIntentWorkflowId = (intent: string): string | undefined => {
    return resolutionConfig.intentWorkflowMappings.find((m) => m.intent === intent)?.workflowId;
  };

  const handleTicketTypeWorkflowChange = (
    ticketTypeId: string,
    workflowId: string | undefined
  ) => {
    const existingMappings = ticketTypeMappings.filter((m) => m.ticketTypeId !== ticketTypeId);

    if (workflowId) {
      const workflow = workflows.find((w) => w.id === workflowId);
      onChange({
        ...resolutionConfig,
        ticketTypeWorkflowMappings: [
          ...existingMappings,
          { ticketTypeId, workflowId, workflowName: workflow?.name, priority: 10 },
        ],
      });
    } else {
      onChange({
        ...resolutionConfig,
        ticketTypeWorkflowMappings: existingMappings,
      });
    }
  };

  const getTicketTypeWorkflowId = (ticketTypeId: string): string | undefined => {
    return ticketTypeMappings.find((m) => m.ticketTypeId === ticketTypeId)?.workflowId;
  };

  const intentMappedCount = resolutionConfig.intentWorkflowMappings.length;
  const ticketTypeMappedCount = ticketTypeMappings.length;
  const toolsEnabledCount = resolutionConfig.availableMcpTools?.length || 0;

  return (
    <Card className="p-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Workflow className="h-5 w-5 text-purple-600" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Resolution Routing</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Configure n8n workflows, MCP tools, and handoff rules
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-6">
          {/* Section Tabs */}
          <div className="flex gap-2 border-b mb-4">
            <button
              onClick={() => setActiveSection('intents')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeSection === 'intents'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              Intent Workflows ({intentMappedCount})
            </button>
            <button
              onClick={() => setActiveSection('ticketTypes')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeSection === 'ticketTypes'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              Ticket Type Workflows ({ticketTypeMappedCount})
            </button>
            <button
              onClick={() => setActiveSection('tools')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeSection === 'tools'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              MCP Tools ({toolsEnabledCount})
            </button>
            <button
              onClick={() => setActiveSection('handoff')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeSection === 'handoff'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              Handoff Rules
            </button>
          </div>

          {/* Intent Workflows */}
          {activeSection === 'intents' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">
                Trigger n8n workflows based on detected intent. Intents without mappings will use default AI handling.
              </p>

              {workflows.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    No n8n workflows configured. Set up workflows in the Workflow Service first.
                  </p>
                </div>
              ) : (
                AI_INTENT_LIST.map((intent) => (
                  <div
                    key={intent.id}
                    className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg dark:bg-gray-900"
                  >
                    <div className="min-w-[180px]">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{intent.label}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{intent.description}</p>
                    </div>

                    <ThemedSelect
                      value={getIntentWorkflowId(intent.id) || ''}
                      onChange={(e) =>
                        handleIntentWorkflowChange(intent.id, e.target.value || undefined)
                      }
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                    >
                      <option value="">-- Default AI handling --</option>
                      {workflows.map((wf) => (
                        <option key={wf.id} value={wf.id}>
                          {wf.name}
                        </option>
                      ))}
                    </ThemedSelect>

                    {getIntentWorkflowId(intent.id) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleIntentWorkflowChange(intent.id, undefined)}
                        className="text-gray-500 dark:text-gray-400"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Ticket Type Workflows */}
          {activeSection === 'ticketTypes' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">
                Trigger n8n workflows based on the ticket&apos;s type. Ticket types without mappings
                will fall back to intent-based routing and default AI handling.
              </p>

              {workflows.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    No n8n workflows configured. Set up workflows in the Workflow Service first.
                  </p>
                </div>
              ) : ticketTypes.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    No active ticket types found. Create ticket types in Settings → Ticket Types first.
                  </p>
                </div>
              ) : (
                ticketTypes.map((ticketType) => (
                  <div
                    key={ticketType.id}
                    className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg dark:bg-gray-900"
                  >
                    <div className="min-w-[180px]">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {ticketType.name}
                      </p>
                      {ticketType.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {ticketType.description}
                        </p>
                      )}
                    </div>

                    <ThemedSelect
                      value={getTicketTypeWorkflowId(ticketType.id) || ''}
                      onChange={(e) =>
                        handleTicketTypeWorkflowChange(ticketType.id, e.target.value || undefined)
                      }
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                    >
                      <option value="">-- Default AI handling --</option>
                      {workflows.map((wf) => (
                        <option key={wf.id} value={wf.id}>
                          {wf.name}
                        </option>
                      ))}
                    </ThemedSelect>

                    {getTicketTypeWorkflowId(ticketType.id) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTicketTypeWorkflowChange(ticketType.id, undefined)}
                        className="text-gray-500 dark:text-gray-400"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* MCP Tools */}
          {activeSection === 'tools' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">
                Enable MCP tools that the AI can use to resolve tickets. Tools will be called based on intent and context.
              </p>

              <div className="grid grid-cols-2 gap-3">
                {AVAILABLE_MCP_TOOLS.map((tool) => {
                  const isEnabled = resolutionConfig.availableMcpTools?.includes(tool.id);

                  return (
                    <div
                      key={tool.id}
                      onClick={() => handleToolToggle(tool.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        isEnabled
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 bg-white hover:border-gray-300 dark:bg-gray-800 dark:border-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Zap
                          className={`h-5 w-5 ${isEnabled ? 'text-primary-600' : 'text-gray-400'} dark:text-gray-500`}
                        />
                        <div>
                          <p
                            className={`text-sm font-medium ${
                              isEnabled ? 'text-primary-900' : 'text-gray-900 dark:text-gray-100'
                            }`}
                          >
                            {tool.label}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{tool.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Handoff Rules */}
          {activeSection === 'handoff' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">
                Configure when tickets should be handed off to human agents instead of AI resolution.
              </p>

              {/* Confidence Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Confidence Threshold
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={resolutionConfig.confidenceThreshold * 100}
                    onChange={(e) =>
                      onChange({
                        ...resolutionConfig,
                        confidenceThreshold: parseInt(e.target.value) / 100,
                      })
                    }
                    className="flex-1"
                  />
                  <span className="text-sm font-medium w-12">
                    {Math.round(resolutionConfig.confidenceThreshold * 100)}%
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                  Hand off to agent if AI confidence is below this threshold
                </p>
              </div>

              {/* Sentiment Threshold */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Negative Sentiment Threshold
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="-100"
                    max="0"
                    value={resolutionConfig.sentimentHandoffThreshold * 100}
                    onChange={(e) =>
                      onChange({
                        ...resolutionConfig,
                        sentimentHandoffThreshold: parseInt(e.target.value) / 100,
                      })
                    }
                    className="flex-1"
                  />
                  <span className="text-sm font-medium w-12">
                    {Math.round(resolutionConfig.sentimentHandoffThreshold * 100)}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                  Hand off to agent if sentiment is more negative than this value
                </p>
              </div>

              {/* VIP Handoff */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-200">VIP Always Handoff</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Always hand off VIP customers to human agents
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={resolutionConfig.vipAlwaysHandoff ?? false}
                    onChange={(e) =>
                      onChange({
                        ...resolutionConfig,
                        vipAlwaysHandoff: e.target.checked,
                      })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 dark:bg-gray-700"></div>
                </label>
              </div>

              {/* Max Resolution Attempts */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Max Resolution Attempts
                </label>
                <input
                  type="number"
                  value={resolutionConfig.maxResolutionAttempts || 3}
                  onChange={(e) =>
                    onChange({
                      ...resolutionConfig,
                      maxResolutionAttempts: parseInt(e.target.value) || 3,
                    })
                  }
                  min={1}
                  max={10}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
                <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                  Hand off after this many failed AI resolution attempts
                </p>
              </div>

              {/* Default Handoff Team */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                  Default Handoff Team
                </label>
                <ThemedSelect
                  value={resolutionConfig.defaultHandoffTeam || ''}
                  onChange={(e) =>
                    onChange({
                      ...resolutionConfig,
                      defaultHandoffTeam: e.target.value || undefined,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-gray-900 dark:border-gray-700 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                >
                  <option value="">-- Use pipeline default --</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </ThemedSelect>
                <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                  Team to receive handoff tickets
                </p>
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mt-6">
            <h4 className="text-sm font-medium text-purple-900 mb-2">Resolution Flow</h4>
            <ol className="text-xs text-purple-800 space-y-1 list-decimal list-inside">
              <li>Email received and processed by AI Engine</li>
              <li>Intent detected</li>
              <li>Check for intent workflow mapping</li>
              <li>Execute MCP tools based on intent strategy</li>
              <li>If handoff conditions met, route to human agent</li>
              <li>Otherwise, send AI-generated response</li>
            </ol>
          </div>
        </div>
      )}
    </Card>
  );
}
